import { authFetch } from 'auth/utils';
import { ErrorCodes } from 'src/common/responseCodes';

import { mapSessionStatusToStringSessionStatus } from '../utils/sessionStatus';

import { fetchAssignedTagToSims } from 'simInventory/Tags/services/tagsSimApi';

import { SimInventorySearchParamsV2, SimInventorySort } from 'simInventory/models/SimProfile.model';

import { preparePaginatedSimInventorySearchBody } from 'simInventory/utils/prepareSimInventorySearchBody';
import { normilizeAssignedTagsToSim } from 'tags/utils/mappers';
import { envVariables } from 'src/common/utils/envVariables';
import { mapSimDTO } from 'src/common/components/Sim/Inventory/utils/simListResponseParsers';
import { fetchSimInventoryTableConfiguration } from './simInventoryTableConfigurationApi';
import { SimDTO, SessionDTO, SessionListDetailsDTO } from 'src/common/components/Sim/constants/sim';
import { SimListResponsePagination } from 'src/common/components/Sim/Inventory/types/simListTypes';
import { ErrorResponse } from 'src/common/types/apiTypes';

export const emptySimProfile = () => {
  const emptyItem: SimDTO = {
    iccid: '',
    imsi: '',
    msisdn: '',
    euicc: '',
    imei: '',
    organisation: '',
    mobileNetworkOperator: '',
    customerCommsPlan: '',
    simType: 'Unknown',
    mnoStatus: 0,
    mnoStatusEntryTime: '',
    sessionStatus: 'Unknown',
    sessionStartTime: '',
    sessionEndTime: '',
    sessionStatusLastUpdated: '',
    apn: '',
    localization: null,
    liveDataUsage: null,
    inboundLiveDataUsage: null,
    outboundLiveDataUsage: null,
    inMinuteUsage: null,
    outMinuteUsage: null,
    inTextUsage: null,
    outTextUsage: null,
    orderNumber: '',
    connectionId: '',
    customerReference: '',
    managementIpAddress: '',
    currentIpAddress: '',
    accountRef: null,
    accountName: '',
    commsPlan: '',
    mnoRatePlan: '',
    accountIdentifier: '',
    rasClient: '',
    framedProtocol: '',
    nasIpAddress: '',
    serviceType: '',
    userName: '',
    timeZone: '',
    cumulativeInputUsage: null,
    cumulativeOutputUsage: null,
    eventTimestamp: '',
    ratType: '',
    tags: [],
    customFields: [],
    activeConfiguration: '',
    installerName: '',
    installerRef: null,
  };
  const normalizedTagsData = normilizeAssignedTagsToSim([]);
  const simDetail = mapSimDTO(emptyItem, {
    normalizedTagsData,
  });

  return {
    ...simDetail,
    profileStatus: mapSessionStatusToStringSessionStatus(emptyItem.sessionStatus),
  };
};

export const fetchSimInventory = async (
  page: number,
  entryPerPage: number,
  searchParams: SimInventorySearchParamsV2,
  sort: SimInventorySort | null = null,
  fetchTags: boolean = true,
  signal?: AbortSignal,
) => {
  const body = preparePaginatedSimInventorySearchBody(page, entryPerPage, searchParams, sort);

  const simInventoryResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_SIMS_PROFILE_API}`,
    {
      signal,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const simInventory: SimDTO[] = await simInventoryResponse.json();

  if (fetchTags) {
    const tags = await fetchAssignedTagToSims({ iccids: simInventory.map((sim) => sim.iccid) });
    const normalizedTagsData = normilizeAssignedTagsToSim(tags);

    return {
      items: simInventory.map((item) => mapSimDTO(item, { normalizedTagsData })),
    };
  }

  return {
    items: simInventory.map((item) => mapSimDTO(item)),
  };
};

export const fetchSimInventoryWithConfiguration = async (
  page: number,
  entryPerPage: number,
  searchParams: SimInventorySearchParamsV2,
  sort: SimInventorySort | null = null,
  fetchTags: boolean = true,
  signal?: AbortSignal,
) => {
  const simInventoryResponse = await fetchSimInventory(
    page,
    entryPerPage,
    searchParams,
    sort,
    fetchTags,
    signal,
  );

  const configuration = await fetchSimInventoryTableConfiguration();
  return {
    ...simInventoryResponse,
    configuration,
  };
};

export const fetchSimInventoryPagination = async (
  page: number,
  entryPerPage: number,
  searchParams: SimInventorySearchParamsV2,
  signal?: AbortSignal,
) => {
  const body = preparePaginatedSimInventorySearchBody(page, entryPerPage, searchParams);

  const simInventoryPaginationResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_SIMS_PROFILE_API}/pagination`,
    {
      signal,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const simInventoryPaginationResponseData: SimListResponsePagination =
    await simInventoryPaginationResponse.json();

  return {
    totalNumberOfItems: simInventoryPaginationResponseData.totalCount ?? -1,
  };
};

export const fetchSimInventoryEntry = async (
  iccid: string,
  returnEmptyOnError?: boolean,
  signal?: AbortSignal,
) => {
  const simInventoryEntryResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_SIMS_PROFILE_API}/${iccid}`,
    { signal },
  );

  if (!simInventoryEntryResponse.ok) {
    const status = simInventoryEntryResponse.status.toString();

    if (returnEmptyOnError) {
      return emptySimProfile();
    }

    if (status === ErrorCodes.NotFound) {
      throw new Error(ErrorCodes.NotFound);
    }

    let errorMessage: ErrorResponse;
    try {
      errorMessage = await simInventoryEntryResponse.json();
    } catch {
      throw new Error(status);
    }

    const iccidNotFoundErrors = errorMessage.validationErrors.Iccid?.find(
      (error) => error.code === 'IccidValidator',
    );

    if (status === ErrorCodes.BadRequest && iccidNotFoundErrors) {
      throw new Error(ErrorCodes.NotFound);
    }

    throw new Error(status);
  }

  const simInventoryItem: SimDTO = await simInventoryEntryResponse.json();

  const tags = await fetchAssignedTagToSims({ iccids: [iccid] });
  const normalizedTagsData = normilizeAssignedTagsToSim(tags);
  const simDetail = mapSimDTO(simInventoryItem, {
    normalizedTagsData,
  });

  return simDetail;
};

export const fetchSimInventoryEntryByImsi = async (imsi: string, signal?: AbortSignal) => {
  const queryParams = new URLSearchParams({ Imsi: imsi });

  const simInventoryResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_SIMS_PROFILE_API}?${queryParams.toString()}`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (!simInventoryResponse.ok) {
    const status = simInventoryResponse.status.toString();

    if (status === ErrorCodes.NotFound) {
      throw new Error(ErrorCodes.NotFound);
    }

    if (status === ErrorCodes.BadRequest) {
      throw new Error(ErrorCodes.NotFound);
    }

    throw new Error(status);
  }

  const simInventory: SimDTO[] = await simInventoryResponse.json();

  return {
    items: simInventory.map((item) => mapSimDTO(item)),
  };
};

export const fetchSessionsListDetails = async (imsis: string[], signal?: AbortSignal) => {
  const body = {
    imsis,
  };
  const simInventoryResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_SIMS_PROFILE_SESSIONS_LIST_API}`,
    {
      signal,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const sessionsDetails: SessionListDetailsDTO[] = await simInventoryResponse.json();

  return sessionsDetails;
};

export const fetchSessionsDetails = async (imsis: string[], signal?: AbortSignal) => {
  const body = {
    imsis,
  };

  const simInventoryResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_SIMS_PROFILE_SESSIONS_API}`,
    {
      signal,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const sessionsDetails: SessionDTO[] = await simInventoryResponse.json();

  return sessionsDetails;
};

export type MaxSimProfilesValidationError = {
  state: {
    maxSimProfilesCount: number;
  };
};
export class MaxSimProfilesCountError extends Error {
  maxSimProfilesCount: number;

  constructor(maxSimProfilesCount: number) {
    super(ErrorCodes.BadRequest);
    Object.setPrototypeOf(this, MaxSimProfilesCountError.prototype);
    this.maxSimProfilesCount = maxSimProfilesCount;
  }
}
export class ReportAlreadyScheduledError extends Error {
  constructor() {
    super(ErrorCodes.BadRequest);
  }
}
